import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';

import { applyConsent, readStoredConsent } from '../analytics/consent';
import { captureFirstTouch, clearFirstTouch } from '../analytics/attribution';

/** Cookie policy lives on the marketing site; the app has no legal pages. */
const COOKIE_POLICY_URL = 'https://www.w-z.com/cookie/';

/** Fired by the "Cookie settings" menu item to reopen the banner. */
export const CONSENT_REOPEN_EVENT = 'wz:consent:reopen';

export function reopenConsentBanner(): void {
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
}

/**
 * Bottom-fixed, non-blocking cookie consent.
 *
 * Mounted OUTSIDE the `showChrome` gate in AppLayout on purpose: CHROMELESS_ROUTES
 * includes `/events/:id/checkout`, the public surface visitors land on straight
 * from w-z.com and where `begin_checkout` fires. A banner that inherited that
 * gate would be invisible on the highest-value page in the funnel while GA4 set
 * cookies there anyway.
 *
 * Bottom rather than the top banner stack (impersonation, profile-completion) so
 * it doesn't participate in their `topOffset` maths or shift page layout.
 */
export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(readStoredConsent() === null);
    const reopen = () => setOpen(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  const choose = useCallback((granted: boolean) => {
    applyConsent(granted);
    // Consent decides the fate of the touch we parked on landing: promote it to
    // the durable record, or erase it. Declining must actually delete what we
    // held, not merely stop sending it.
    if (granted) captureFirstTouch();
    else clearFirstTouch();
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-label="Cookie consent"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1400,
        bgcolor: '#22303F',
        color: '#fff',
        px: { xs: 2, sm: 3 },
        py: 1.75,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1.5,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
        We use cookies to measure how this site is used. See our{' '}
        <Link href={COOKIE_POLICY_URL} target="_blank" rel="noopener" sx={{ color: '#8EC5FF' }}>
          cookie policy
        </Link>
        .
      </Typography>

      {/* Equal visual weight: rejecting must be as easy as accepting. */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
        <Button
          onClick={() => choose(false)}
          variant="outlined"
          size="medium"
          sx={{ flex: { xs: 1, sm: 'none' }, color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }}
        >
          Reject
        </Button>
        <Button
          onClick={() => choose(true)}
          variant="contained"
          size="medium"
          sx={{ flex: { xs: 1, sm: 'none' } }}
        >
          Accept
        </Button>
      </Box>
    </Box>
  );
}
